import { type DefaultTheme } from 'styled-components';
import { type BaseTheme } from '#/types/styled';

// TODO: Remove this exception once the theme migration is complete
/* eslint-disable @typescript-eslint/no-unused-vars */

/**
 * The pattern to follow is pick from the palette and apply to a constant
 * variable that describes the element or component you are styling.
 * If style applies to both themes, it should be placed in BASE_THEME.
 */

/**
 * TriMet Color Palette
 */

const white = '#ffffff';
const lightestGray = '#f5f5f5';
const lighterGray = '#e9e9e9';
const lightGray = '#ccc';
const gray = '#a6a6a6';
const darkGray = '#808080';
const darkerGray = '#515151';
const darkestGray = '#2b2b2b';
const charcoal = '#222222';
const black = '#000000';

const lightestBlue = '#E6ECF4';
const lighterBlue = '#B5C8DE';
const lightBlue = '#86A4C7';
const blue = '#084c8d'; // TriMet Blue
const darkBlue = '#003d78';
const darkerBlue = '#00386E';
const darkestBlue = '#002b54';

const lightestDeepBlue = '#4170a4';
const lighterDeepBlue = '#335780';
const lightDeepBlue = '#1f354e';
const deepBlue = '#152435'; // TriMet Deep Blue
const darkDeepBlue = '#111e2d';
const darkerDeepBlue = '#08121d';
const darkestDeepBlue = '#03070e';

const lightestActionBlue = '#8bd3fc';
const lighterActionBlue = '#59bfff';
const lightActionBlue = '#24a7ff';
const actionBlue = '#008DEB'; // Represents map action color + hover
const darkActionBlue = '#0074C2';
const darkerActionBlue = '#0063A8';
const darkestActionBlue = '#005485';

const lightestGreen = '#CEEEC3';
const lighterGreen = '#B0DCA1';
const lightGreen = '#82C26B';
const green = '#61A744'; // TriMet Green
const darkGreen = '#3A840B';
const darkerGreen = '#2D6E00';
const darkestGreen = '#305E13';

const lightestRed = '#eaa296';
const lighterRed = '#dd7c6e';
const lightRed = '#ce5345';
const red = '#c63c2f'; // TriMet Red, Alert red,...
const darkRed = '#ac3327';
const darkerRed = '#922a20';
const darkestRed = '#7a2119';

const lightestYellow = '#fcf6d2';
const lighterYellow = '#faf2ba';
const lightYellow = '#f8eda1';
const yellow = '#F7E886'; // TriMet Yellow
const darkYellow = '#D6C974';
const darkerYellow = '#BFAF5E';
const darkestYellow = '#877449';

const lightestOrange = '#f0a693';
const lighterOrange = '#e58168';
const lightOrange = '#d85a3b';
const orange = '#d1441e'; // TriMet Orange
const darkOrange = '#b53a19';
const darkerOrange = '#81260e';
const darkestOrange = '#501405';
const biketownRentalLogoOrange = '#FF4D00';

const cyanDark = '#008ab0';
const transparent = 'transparent';

/**
 * A few values that are used in multiple themes
 * and or have specific use cases.
 */

const mapElementShadow = '0 1px 1.5px';
const mapElementShadowHover = '0 0 3px';
const contentElementShadow = '0 1px 3px';
const contentElementShadowHover = '0 0 5px';
const contentPanelShadow = '0 2px 8px';
const imageOverlayFrom = `rgba(8, 76, 141, 0.65)`;
const imageOverlayTo = `rgba(8, 76, 141, 0.65)`;
// TODO(PRJ77-1158): clean up input radius values
const inputRadius = '5px';
const textInputRadius = '8px';
const inputShadow = '#0000001A';
const contentLegacyBg = '#0a559e';

/**
 * Some of the focus indicator uses are outside of the ThemeContext
 * Currently all themes have same focus styles, so exporting this directly for now
 */
export const FOCUS_INDICATOR = {
  inner: white,
  outer: black,
} as const;

/**
 * The BASE_THEME should have constants that are shared between the light and dark themes.
 */

const BASE_THEME: BaseTheme = {
  // Context Menu
  contextMenuButtonColor: gray,
  contextMenuButtonHoverColor: darkerGray,

  // Opacity helpers
  opacity_50: 0.5,
  opacity_70: 0.7,
  opacity_25: 0.25,

  white_15: 'rgba(255, 255, 255, 0.15)',
  white_25: 'rgba(255, 255, 255, 0.25)',
  white_32: 'rgba(255, 255, 255, 0.32)',
  white_50: 'rgba(255, 255, 255, 0.5)',
  white_65: 'rgba(255, 255, 255, 0.65)',
  white_85: 'rgba(255, 255, 255, 0.85)',

  black_15: 'rgba(0, 0, 0, 0.15)',
  black_25: 'rgba(0, 0, 0, 0.25)',
  black_32: 'rgba(0, 0, 0, 0.32)',
  black_50: 'rgba(0, 0, 0, 0.5)',
  black_65: 'rgba(0, 0, 0, 0.65)',
  black_85: 'rgba(0, 0, 0, 0.85)',

  // Buttons
  buttonRadiusSmall: '3px',
  buttonRadius: '5px',
  buttonRadiusLarge: '8px',
  buttonRadiusXLarge: '12px',
  buttonRounded: '10em',

  buttonGapSmall: '4px',
  buttonGap: '6px',
  buttonGapLarge: '9px',

  // Radius helpers
  topRadius: '10px 10px 0 0',
  bottomRadius: '0 0 10px 10px',
  mapPopupRadius: '10px',

  // Inputs
  inputDropDownRadius: `0 0 ${inputRadius} ${inputRadius}`,
  inputOpenRadius: `${inputRadius} ${inputRadius} 0 0`,
  inputDropDownShadow: `0 9px 15px ${inputShadow}`,
  inputShadow: `0 0 5px ${inputShadow}`,
  inputRadius,
  textInputRadius,

  // Transitions
  boxShadowTransition: 'box-shadow 0.25s ease-in-out',

  // Icon helpers
  parkRideButtonBg: black,
  warningRed: red,

  // Print page
  printPageBg: white,
  printTripDetailsBg: lighterGray,

  // LEGACY COLORS
  stopHighlightColor: '#5cc3ff',
  stopBladeIconHighlight: '#5bc4fc',
  activeOmniboxULLIColor: 'rgb(51, 122, 183)',
  newOmniboxULLIColor: '#dedede',
  trimetOrange: '#d14727',
  trimetHighlight: '#F7E886',
  originColor: '#6cb33f',
  selectedStopIconColor: '#6DB33F',
  destinationColor: '#e53333',
  activeButtonColor: '#6DB33F',
  activeButtonHover: '#609E38',
  activeButtonHover2: '#e0e0e0',
  activeButtonFocus: '#609E38',
  currentPositionColor: '#62bd19',
  clearCurrentPositionColor: '#969696',
  selectedStopColor: '#efecbe',
  activeOmniboxSpanColor: '#084c8d',
  hoverOpacity: 0.6,
  readOnlyOpacity: 0.7,
  inactiveOpacity: 0.6,
  initialButtonOpacity: 0.65,
  realtimeNotificationColor: '#007494',
  tabActiveBorder: '#6cb33e',
  tabBackgroundColor: '#084c8d',
  tabColor: 'rgba(255,255,255,.6)',
  tabActiveColor: 'rgba(255,255,255,1)',
  routeDetailBadgeBorder: '#fff',
  routeStopFillColor: '#fff',
  routeStopStrokeColor: '#000',
  warningBackground: '#000000de',
  warningButtonHoverBackground: '#ffffff33',
  warningFontColor: '#fff',
  warningFontColorEmphasis: '#e7b008',
  textShadow: '0 1px 3px rgba(0,0,0,.25)',
  textShadowHover: '0 1px 4px rgba(0,0,0,.55)',
  closeShadow: 'drop-shadow(0 0 2px rgb(0 0 0 / 0.4))',
  closeShadowHover: 'drop-shadow(0 0 4px rgb(0 0 0 / 0.6))',
  buttonShadow: '0 1px 8px rgba(0,0,0,0.2)',
  buttonShadowHover: '0 0 5px rgba(0,0,0,0.2)',
  boxShadowPanel: '0 2px 10px rgba(0,0,0,0.4)',
  boxShadowInput: '0 2px 10px rgba(0,0,0,0.4)',
  boxShadowInputHover: '0 2px 10px rgba(0,0,0,0.6)',
  boxShadowCardHover: '0 0 5px rgba(0,0,0,0.3)',
  elementsTransition: 'all 0.25s ease',
  locationMarkerColor: '#3b7fc1',
  warnColor: '#ff8800',
  dangerColor: '#b50000',
  dangerColorHover: '#ba3d1b',
  baseAlertBackgroundColor: '#b03823',
  baseAlertHoverColor: '#c53f27',
  baseAlertBorderColor: '#a13320',

  alerts: {
    // alerts > normal
    normal: {
      background: lighterYellow,
      backgroundHover: lightestYellow,
      border: darkestYellow,
      text: charcoal,
      textLink: darkerGray,
      iconFill: red,
      iconFillReverse: white,
    },
    // alerts > priority
    priority: {
      background: red,
      backgroundHover: darkRed,
      border: lightGray,
      text: white,
      textLink: lightestGray,
      iconFill: white,
      iconFillReverse: red,
    },
    // alerts > snow
    snow: {
      background: darkActionBlue,
      backgroundHover: darkerActionBlue,
      border: lightGray,
      text: white,
      textLink: lightestGray,
      iconFill: white,
      iconFillReverse: darkActionBlue,
    },
  },

  checkMarkColor: '#00a83e',
  notificationColor: '#d59f01',
  focusVisible: '#6cb33e',
  menuFooterBg: '#000',
  tripTopSkeleton: '#e9e9e9',
  tripStepSkeleton: '#f4f4f4',
  tripTopSkeletonTop: '#d4d4d4',
  stopTopSkeletonMain: '#003d78',
  stopTopSkeletonHighlight: '#084c8d',
  blue_15: 'rgba(8, 76, 141, 0.15)',
  blue_25: 'rgba(8, 76, 141, 0.25)',
  blue_50: 'rgba(8, 76, 141, 0.5)',
  white: '#fff',
  infoColor: '#939393',
} as const;

/**
 * LIGHT THEME
 */

export const LIGHT_THEME: DefaultTheme = {
  ...BASE_THEME,
  name: 'Light',

  // Buttons

  // -> Primary Button
  primaryButtonBg: darkGreen,
  primaryButtonBgHover: darkerGreen,
  primaryButtonBorder: darkGreen,
  primaryButtonBorderHover: darkerGreen,
  primaryButtonText: white,
  // -> Outline Button
  outlineButtonBg: 'transparent',
  outlineButtonBgHover: darkerGreen,
  outlineButtonBorder: charcoal,
  outlineButtonBorderHover: darkerGreen,
  outlineButtonText: charcoal,
  outlineButtonTextHover: white,
  // -> Outline Reverse Button
  outlineReverseButtonBg: 'transparent',
  outlineReverseButtonBgHover: darkerGreen,
  outlineReverseButtonBorder: charcoal,
  outlineReverseButtonBorderHover: darkerGreen,
  outlineReverseButtonText: charcoal,
  outlineReverseButtonTextHover: white,
  // -> Tertiary Button
  tertiaryButtonBg: white,
  tertiaryButtonBgHover: white,
  tertiaryButtonBorder: gray,
  tertiaryButtonBorderHover: darkGray,
  tertiaryButtonText: lightestDeepBlue,
  tertiaryButtonTextHover: lightDeepBlue,
  // -> Tertiary Reverse Button
  tertiaryReverseButtonBg: white,
  tertiaryReverseButtonBgHover: white,
  tertiaryReverseButtonBorder: gray,
  tertiaryReverseButtonBorderHover: darkGray,
  tertiaryReverseButtonText: lightestDeepBlue,
  tertiaryReverseButtonTextHover: lightDeepBlue,

  // => Text Button
  textButtonBgHover: darkerGreen,
  textButtonBg: 'transparent',
  textButtonBorder: 'transparent',
  textButtonBorderHover: darkerGreen,
  textButtonText: charcoal,
  textButtonTextHover: white,
  // => Text Reverse Button
  textReverseButtonBgHover: darkerGreen,
  textReverseButtonBg: 'transparent',
  textReverseButtonBorder: 'transparent',
  textReverseButtonBorderHover: darkerGreen,
  textReverseButtonText: charcoal,
  textReverseButtonTextHover: white,
  // -> Map Button
  mapButtonBg: white,
  mapButtonIcon: darkerGray,
  mapButtonText: darkerGray,
  mapButtonIconHover: black,
  mapButtonTextHover: black,
  mapButtonShadow: `${mapElementShadow} ${gray}`,
  mapButtonShadowHover: `${mapElementShadowHover} ${darkGray}`,

  // Text-link
  textLink: lightestDeepBlue,
  textLinkHover: lightDeepBlue,

  // Text-link-reverse
  textLinkReverse: lightestDeepBlue,
  textLinkReverseHover: lightDeepBlue,

  // Map Focus
  mapFocusOutline: black,

  // Menu
  menuBg: lightestGray,
  menuWrapperBg: charcoal,
  menuPrimarySecondaryText: charcoal,
  menuSecondaryLabels: charcoal,
  menuIconFill: white,
  menuAnimatedUnderline: green,
  menuFooterText: gray,
  menuFooterTextHover: white,
  menuFooterBullet: white,
  menuPromoChevronRight: orange,
  menuPromoText: white,
  menuMobileCloseIcon: charcoal,
  menuPromoOverlay: `linear-gradient(${imageOverlayFrom} 0%, ${imageOverlayTo} 100%)`,
  menuNavBg: blue,
  menuLogoText: blue,

  // Navbar (Logo, Title, Save/Close Buttons)
  navbarBg: blue,
  navbarButtonHover: darkerBlue,
  navbarText: white,
  navbarTextHover: BASE_THEME.white_50,
  navbarMenuButtonBg: darkGreen,
  navbarMenuButtonBgHover: darkerGreen,
  navbarStripe: orange,

  // -> Skip Nav Link
  skipNavLinkBg: darkGreen,
  skipNavLinkText: white,

  // DetailNavHeader (Back, MinMax, Close buttons)
  navHeaderBg: `linear-gradient(360deg, ${lightGray} 0%, ${white} 100%);`,
  navHeaderTitle: charcoal,
  navIconFill: charcoal,
  navIconBgHover: lightGray,
  navMinMaxBg: lighterGray,
  navMinMaxBgHover: lightestGray,
  navMinMaxBorder: gray,

  // SidePanel content
  contentBg: lightestGray,
  contentBgAlt: lightestGray,
  contentBgHeader: lighterGray,
  contentBgDarken: lightestGray,
  contentChevronDivider: darkerGray,
  contentHeaderBg: lightGray,
  contentText: charcoal,
  contentTextAlt: charcoal,
  contentTextDim: darkerGray,
  contentTextDimAlt: darkerGray,
  contentTextError: red,
  contentTextInfo: darkerGray,
  contentDivider: lightGray,
  contentPanelShadow: `${contentPanelShadow} ${BASE_THEME.black_50}`,

  // Tabs
  tabListBg: lightestGray,
  tabText: charcoal,
  tabTextActive: charcoal,
  tabBorderActive: darkGreen,

  // Card
  cardBg: white,
  cardBorder: lightGray,
  cardShadow: `${contentElementShadow} ${gray}`,
  cardShadowHover: `${contentElementShadowHover} ${darkGray}`,
  cardExternalLink: charcoal,
  cardExternalLinkBgHover: lighterGray,
  cardText: charcoal,
  cardTextDim: darkerGray,
  cardOutlineBorderHover: darkerGray,
  cardOutlineBorder: lightGray,

  // Arrivals
  arrivalSchBg: lighterGray,
  arrivalBorder: charcoal,
  arrivalEstText: charcoal,
  arrivalSchText: darkerGray,
  arrivalTrackText: white,
  arrivalTrackBg: darkActionBlue,
  arrivalTrackBgHover: darkerActionBlue,
  arrivalTrackDropoffCanceledText: white,
  arrivalDropoffCanceled: darkRed,
  arrivalDropoffCanceledDarken: darkerRed,

  // Favorite star
  favoriteStarFill: darkRed,
  favoriteStarStroke: charcoal,

  // Input text
  inputBg: white,
  inputText: charcoal,
  inputIconFill: darkerGray,
  inputIconFillHover: black,
  inputBorder: `1px solid ${lightGray}`,
  inputTextPlaceholder: darkerGray,
  inputOptionalText: darkerGray,

  // Map Popup
  mapPopupShadow: `drop-shadow(${mapElementShadow} ${darkGray})`,
  mapPopupBg: white,
  mapPopupText: charcoal,
  mapPopupTextLabel: darkerGray,
  mapPopupButtonBgHover: lightestGray,
  mapPopupButtonBorder: darkestGray,
  mapPopupButtonHover: darkestGreen,
  mapPopupButtonText: charcoal,
  mapPopupButtonTextHover: white,
  mapPopupClose: darkGray,
  mapPopupCloseHover: black,

  // Icon Helpers
  biketownRentalLogoFill: biketownRentalLogoOrange,
  exclamationMutedIconBg: charcoal,
  transitTrackerIconFill: cyanDark,
  extraMapControlsIconFill: charcoal,

  // PopoverMenuList
  popoverMLEllipsesFill: darkerGray,
  popoverMLEllipsesFillHover: black,
  popoverMLBg: white,
  popoverMLBorder: gray,
  popoverMLItemHover: darkGreen,
  popoverMLText: charcoal,
  popoverMLTextHover: white,

  // Vehicle tracking
  vehicleTrackerHighlightColor: actionBlue,
  vehicleTrackerLowlightColor: lightestBlue,
  vehicleTrackerHighlightBackingColor: lightestGray,
  vehicleTrackerBaseColor: transparent,
  vehicleContentSelectedStopHighlightBg: darkActionBlue,
  vehicleContentSelectedStopHighlightArrivalText: darkActionBlue,
  vehicleContentSelectedStopHighlightText: white,
  vehicleContentSelectedMapIconFill: lighterGray,
  vehicleContentSelectedMapIconFillHover: white,
  vehicleContentDropOffOnlyBg: darkRed,
  vehicleContentDropOffOnlyText: white,

  // Toast
  toastCardShadow: `${mapElementShadow} ${gray}`,
  toastCloseFrontCircle: charcoal,
  toastCloseBackCircle: white,
  toastCloseFill: darkerGray,
  toastText: charcoal,

  // Inputs: Drop downs, all things around and within...
  dropDownContentBg: white,
  dropDownInputBg: white,
  dropDownIndicator: charcoal,
  dropDownText: charcoal,
  dropDownTextSelected: white,
  dropDownTextDim: gray,
  dropDownLabelText: charcoal,
  dropDownBorder: lightGray,
  dropDownInputRadius: BASE_THEME.inputOpenRadius,
  dropDownContentRadius: BASE_THEME.inputDropDownRadius,
  dropDownInputShadow: BASE_THEME.inputDropDownShadow,
  dropDownContentShadow: BASE_THEME.inputDropDownShadow,
  dropDownOptionSelectedCheck: lighterGreen,
  dropDownOptionSelected: darkGreen,
  dropDownOptionSelectedHover: lightestGreen,
  dropDownTimeInputFocus: lighterGreen,
  dropDownTimeDateTabFocus: lightGreen,
  dropDownEditBg: lighterGray,
  dropDownEditBgHover: lightestGray,
  dropDownSaveCloseBgHover: lighterGray,

  // TODO: (PRJ77-727) Update these with actual colors for the given theme
  // Checkbox
  checkboxBorder: darkGray,
  checkboxBorderHover: gray,
  checkboxCheck: white,
  checkboxCheckHover: white,
  checkboxCheckDisabled: white,
  checkboxCheckedBg: darkGreen,
  checkboxLabelText: charcoal,

  // Map Attribution
  mapAttributionDesktopText: darkerGray,
  mapAttributionDesktopTextHover: black,
  mapAttributionDesktopBg: white,
  mapAttributionMobileText: gray,
  mapAttributionMobileTextHover: white,
  mapAttributionMobileBg: black,

  // Toggle Options (Used primary in Map Settings)
  toggleOptionBgHover: lightestGray,
  toggleOptionBorder: darkGray,
  toggleOptionBorderActive: lighterGray,
  toggleOptionCheckCircleBg: 'transparent',
  toggleOptionCheckCircleBgActive: darkGreen,
  toggleOptionCheckFill: white,
  toggleOptionText: charcoal,

  // Dialog (Map Settings, etc.)
  dialogBg: white,
  dialogNavText: charcoal,
  dialogText: charcoal,

  // Itinerary

  // -> Trip Summary
  itineraryTripSummaryBg: lighterGray,
  // -> NotificationContainer
  itineraryTextRealtime: darkActionBlue,
  // -> ItineraryBody
  itineraryDividerBorder: lightGray,
  itineraryTipBg: lighterGray,
  itineraryTipLink: blue,
  itineraryExternalTripLink: lightestDeepBlue,
  itineraryExternalTripLinkHover: lightDeepBlue,
  // -> Promos
  itineraryPromoLink: blue,
  itineraryPromoLinkHover: darkBlue,
  // -> Footer
  itineraryFooterBg: lighterGray,
  itineraryLegalLink: darkerGray,
  itineraryLegalLinkHover: charcoal,
  itineraryFooterButtonBg: lightestGray,
  itineraryFooterButtonBgHover: white,
  itineraryFooterButtonText: charcoal,
  itineraryFooterButtonBorder: lightGray,

  // -> Alerts
  itineraryAlertToggleText: red,
  itineraryAlertTextColor: charcoal,
  itineraryAlertBgColor: lighterGray,
  itineraryAlertLinkColor: blue,
  itineraryAlertLinkHoverColor: darkerBlue,
  itineraryAlertDateColor: darkerGray,

  // Skeletons

  skeletonBase: lightGray,
  skeletonHighlight: lightestGray,
  skeletonBaseAlt: lightGray,
  skeletonHighlightAlt: lightestGray,

  // Logo helpers
  fullLogoText: blue,
  fullLogoTextAlt: blue,

  // The code above is the new light mode, organized by component
  // The code below is the old light mode, which should be replaced by the new light mode

  mainColor: '#084c8d',
  secondaryColor: '#385979',
  tertiaryColor: '#e8eef4',
  backgroundColor: '#f5f5f5',
  secondaryBackgroundColor: '#ffffff',
  primaryFontColor: '#222',
  primaryFontColorReverse: '#fff',
  secondaryFontColor: '#575757',
  popperFontColor: '#707070',
  hoverColor: '#f5f5f5',
  activeColor: '#dedede',
  hoverColorInverted: '#4573a1',
  activeColorInverted: '#28598a',
  focusColorInverted: '#5c8ab9',
  badgeBorderColor: 'transparent',
  successColor: '#008ab0',
  actionColor: '#4777a6',
  borderColor: '#efefef',
  buttonColor1: '#0080A3',
  buttonColor1Hover: '#007191',
  buttonColor2: '#efefef',
  buttonText1: '#fff',
  buttonText2: '#555',
  inverseSecondaryButtonBgColor: '#dedede',
  inverseSecondaryButtonBgColorHover: '#cecece',
  inverseBackgroundColor1: '#084c8d',
  inverseBackgroundColor2: '#0a559e',
  inverseBackgroundColor3: '#003d78',
  inverseBackgroundColor4: '#002B54',
  inverseFontColor1: '#ecf6ff',
  inverseFontColor2: '#A5C0D5',
  inverseFontColor3: '#084c8d',
  inverseButtonColor1: '#3F70A2',
  inverseButtonColor2: '#6896c4',
  inverseButtonColor3: '#86abd0',
  inactiveColor: '#808080',
  inputBorderColor: '#cecece',
  inputBorderColorFocus: '#000',
  inputBorderColorReverse: '#3F70A2',
  inputBorderColorFocusReverse: '#fff',
  inputBgColorReverse: '#084c8d',
  inputBgColorReverseFocus: '#003d78',
  stroke: '#e8eef4',
  cardBackgroundColor: '#0D5EAC',
  tripModeLegacyBg: '#0D5EAC',
  dueTextColor: '#4E802D',
  skeletonBaseColor: '#d5d5d5',
} as const;

/**
 * DARK THEME
 */

export const DARK_THEME: DefaultTheme = {
  ...BASE_THEME,
  name: 'Dark',

  // Buttons

  // -> Primary Button
  primaryButtonBg: darkGreen,
  primaryButtonBgHover: darkerGreen,
  primaryButtonBorder: darkGreen,
  primaryButtonBorderHover: darkerGreen,
  primaryButtonText: white,
  // -> Outline Button
  outlineButtonBg: 'transparent',
  outlineButtonBgHover: darkerGreen,
  outlineButtonBorder: white,
  outlineButtonBorderHover: darkerGreen,
  outlineButtonText: white,
  outlineButtonTextHover: white,
  // -> Outline Reverse Button
  outlineReverseButtonBg: 'transparent',
  outlineReverseButtonBgHover: darkerGreen,
  outlineReverseButtonBorder: white,
  outlineReverseButtonBorderHover: darkerGreen,
  outlineReverseButtonText: white,
  outlineReverseButtonTextHover: white,
  // -> Tertiary Button
  tertiaryButtonBg: lightDeepBlue,
  tertiaryButtonBgHover: deepBlue,
  tertiaryButtonBorder: darkGray,
  tertiaryButtonBorderHover: lighterBlue,
  tertiaryButtonText: lightGray,
  tertiaryButtonTextHover: white,
  // -> Tertiary Reverse Button
  tertiaryReverseButtonBg: lightDeepBlue,
  tertiaryReverseButtonBgHover: deepBlue,
  tertiaryReverseButtonBorder: darkGray,
  tertiaryReverseButtonBorderHover: lighterBlue,
  tertiaryReverseButtonText: lighterGray,
  tertiaryReverseButtonTextHover: white,
  // -> Text Button
  textButtonBgHover: darkerGreen,
  textButtonBg: 'transparent',
  textButtonBorder: 'transparent',
  textButtonBorderHover: darkerGreen,
  textButtonText: white,
  textButtonTextHover: white,
  // -> Text Reverse Button
  textReverseButtonBgHover: darkerGreen,
  textReverseButtonBg: 'transparent',
  textReverseButtonBorder: 'transparent',
  textReverseButtonBorderHover: darkerGreen,
  textReverseButtonText: white,
  textReverseButtonTextHover: white,
  // -> Map Button
  mapButtonBg: darkDeepBlue,
  mapButtonIcon: lightGray,
  mapButtonText: lightGray,
  mapButtonIconHover: white,
  mapButtonTextHover: white,
  mapButtonShadow: `${mapElementShadow} ${gray}`,
  mapButtonShadowHover: `${mapElementShadowHover} ${white}`,

  // Text-link
  textLink: lightBlue,
  textLinkHover: lightestBlue,

  // Text-link-reverse
  textLinkReverse: lightBlue,
  textLinkReverseHover: lightestBlue,

  // Map Focus
  mapFocusOutline: white,

  // Menu
  menuBg: darkDeepBlue,
  menuWrapperBg: charcoal,
  menuPrimarySecondaryText: white,
  menuSecondaryLabels: lightestGray,
  menuIconFill: white,
  menuAnimatedUnderline: green,
  menuFooterText: gray,
  menuFooterTextHover: white,
  menuFooterBullet: white,
  menuPromoChevronRight: orange,
  menuPromoText: white,
  menuMobileCloseIcon: white,
  menuPromoOverlay: `linear-gradient(${imageOverlayFrom} 0%, ${imageOverlayTo} 100%)`,
  menuNavBg: blue,
  menuLogoText: white,

  // Navbar (Logo, Title, Save/Close Buttons)
  navbarBg: darkerDeepBlue,
  navbarButtonHover: darkestDeepBlue,
  navbarText: white,
  navbarTextHover: BASE_THEME.white_50,
  navbarMenuButtonBg: darkerGreen,
  navbarMenuButtonBgHover: darkestGreen,
  navbarStripe: darkOrange,

  // -> Skip Nav Link
  skipNavLinkBg: darkGreen,
  skipNavLinkText: white,

  // DetailNavHeader (Back, MinMax, Close buttons)
  navHeaderBg: `linear-gradient(180deg, ${darkestDeepBlue} 0%, ${deepBlue} 100%);`,
  navHeaderTitle: white,
  navIconFill: white,
  navIconBgHover: lightDeepBlue,
  navMinMaxBg: darkDeepBlue,
  navMinMaxBgHover: darkerDeepBlue,
  navMinMaxBorder: lighterDeepBlue,

  // SidePanel content
  contentBg: darkerDeepBlue,
  contentBgAlt: darkerDeepBlue,
  contentBgHeader: darkestDeepBlue,
  contentBgDarken: darkestDeepBlue,
  contentChevronDivider: lightGray,
  contentHeaderBg: darkestDeepBlue,
  contentText: white,
  contentTextAlt: white,
  contentTextDim: lightGray,
  contentTextDimAlt: lightGray,
  contentTextError: lighterRed,
  contentTextInfo: lightGray,
  contentDivider: darkerGray,
  contentPanelShadow: `${contentPanelShadow} ${BASE_THEME.white_50}`,

  // Tabs
  tabListBg: darkDeepBlue,
  tabText: lightestBlue,
  tabTextActive: white,
  tabBorderActive: green,

  // Card
  cardBg: deepBlue,
  cardBorder: darkGray,
  cardShadow: `${contentElementShadow} ${gray}`,
  cardShadowHover: `${contentElementShadowHover} ${white}`,
  cardExternalLink: white,
  cardExternalLinkBgHover: lightDeepBlue,
  cardText: white,
  cardTextDim: gray,
  cardOutlineBorder: darkGray,
  cardOutlineBorderHover: lightGray,

  // Arrivals
  arrivalSchBg: lightDeepBlue,
  arrivalBorder: lightGray,
  arrivalEstText: white,
  arrivalSchText: lighterGray,
  arrivalTrackText: white,
  arrivalTrackBg: darkActionBlue,
  arrivalTrackBgHover: darkerActionBlue,
  arrivalTrackDropoffCanceledText: charcoal,
  arrivalDropoffCanceled: lighterRed,
  arrivalDropoffCanceledDarken: lightestRed,

  // Favorite star
  favoriteStarFill: lighterRed,
  favoriteStarStroke: white,

  // Input text
  inputBg: deepBlue,
  inputText: white,
  inputIconFill: lightGray,
  inputIconFillHover: white,
  inputBorder: `1px solid ${darkGray}`,
  inputTextPlaceholder: lightGray,
  inputOptionalText: lightGray,

  // Map Popup
  mapPopupShadow: `drop-shadow(${mapElementShadow} ${gray})`,
  mapPopupBg: darkDeepBlue,
  mapPopupText: white,
  mapPopupTextLabel: lightGray,
  mapPopupButtonBgHover: lightDeepBlue,
  mapPopupButtonBorder: white,
  mapPopupButtonHover: darkGreen,
  mapPopupButtonText: white,
  mapPopupButtonTextHover: white,
  mapPopupClose: lightGray,
  mapPopupCloseHover: white,

  // Icon Helpers
  biketownRentalLogoFill: biketownRentalLogoOrange,
  exclamationMutedIconBg: lighterGray,
  transitTrackerIconFill: white,
  extraMapControlsIconFill: white,

  // PopoverMenuList
  popoverMLEllipsesFill: lightestGray,
  popoverMLEllipsesFillHover: white,
  popoverMLBg: darkDeepBlue,
  popoverMLBorder: darkGray,
  popoverMLItemHover: darkerGreen,
  popoverMLText: white,
  popoverMLTextHover: white,

  // Vehicle tracking
  vehicleTrackerHighlightColor: actionBlue,
  vehicleTrackerLowlightColor: lightestBlue,
  vehicleTrackerHighlightBackingColor: darkerDeepBlue,
  vehicleTrackerBaseColor: transparent,
  vehicleContentSelectedStopHighlightBg: darkActionBlue,
  vehicleContentSelectedStopHighlightArrivalText: actionBlue,
  vehicleContentSelectedStopHighlightText: white,
  vehicleContentSelectedMapIconFill: lighterGray,
  vehicleContentSelectedMapIconFillHover: white,
  vehicleContentDropOffOnlyBg: red,
  vehicleContentDropOffOnlyText: white,

  // Toast
  toastCardShadow: `${mapElementShadow} ${gray}`,
  toastCloseFrontCircle: white,
  toastCloseBackCircle: deepBlue,
  toastCloseFill: lighterGray,
  toastText: white,

  // Inputs: Drop downs, all things around and within...
  dropDownContentBg: lightDeepBlue,
  dropDownInputBg: lightDeepBlue,
  dropDownIndicator: white,
  dropDownText: white,
  dropDownTextSelected: charcoal,
  dropDownTextDim: lightGray,
  dropDownLabelText: white,
  dropDownBorder: darkGray,
  dropDownInputRadius: BASE_THEME.inputOpenRadius,
  dropDownContentRadius: BASE_THEME.inputDropDownRadius,
  dropDownInputShadow: BASE_THEME.inputDropDownShadow,
  dropDownContentShadow: BASE_THEME.inputDropDownShadow,
  dropDownOptionSelectedCheck: charcoal,
  dropDownOptionSelected: lightGreen,
  dropDownOptionSelectedHover: darkerGreen,
  dropDownTimeInputFocus: lighterGreen,
  dropDownTimeDateTabFocus: lighterGreen,
  dropDownEditBg: deepBlue,
  dropDownEditBgHover: darkerDeepBlue,
  dropDownSaveCloseBgHover: darkDeepBlue,

  // TODO: (PRJ77-727) Update these with actual colors for the given theme
  // Checkbox
  checkboxBorder: lightGray,
  checkboxBorderHover: gray,
  checkboxCheck: white,
  checkboxCheckHover: white,
  checkboxCheckDisabled: white,
  checkboxCheckedBg: darkGreen,
  checkboxLabelText: white,

  // Map Attribution
  mapAttributionDesktopText: lightGray,
  mapAttributionDesktopTextHover: white,
  mapAttributionDesktopBg: black,
  mapAttributionMobileText: gray,
  mapAttributionMobileTextHover: white,
  mapAttributionMobileBg: black,

  // Toggle Options (Used primary in Map Settings)
  toggleOptionBgHover: darkestDeepBlue,
  toggleOptionBorder: lightGray,
  toggleOptionBorderActive: lighterGray,
  toggleOptionCheckCircleBg: 'transparent',
  toggleOptionCheckCircleBgActive: darkGreen,
  toggleOptionCheckFill: white,
  toggleOptionText: white,

  // Dialog (Map Settings, etc.)
  dialogBg: darkDeepBlue,
  dialogNavText: white,
  dialogText: white,

  // Itinerary

  // -> Trip Summary
  itineraryTripSummaryBg: darkestDeepBlue,
  // -> NotificationContainer
  itineraryTextRealtime: actionBlue,
  // -> ItineraryBody
  itineraryDividerBorder: gray,
  itineraryTipBg: darkDeepBlue,
  itineraryTipLink: lightBlue,
  itineraryExternalTripLink: lighterBlue,
  itineraryExternalTripLinkHover: lightestBlue,
  // -> Promos
  itineraryPromoLink: lightBlue,
  itineraryPromoLinkHover: lighterBlue,
  // -> Footer
  itineraryFooterBg: darkestDeepBlue,
  itineraryLegalLink: lightBlue,
  itineraryLegalLinkHover: lightestBlue,
  itineraryFooterButtonBg: darkDeepBlue,
  itineraryFooterButtonBgHover: darkerDeepBlue,
  itineraryFooterButtonText: white,
  itineraryFooterButtonBorder: lightDeepBlue,

  // -> Alerts
  itineraryAlertToggleText: lighterRed,
  itineraryAlertTextColor: white,
  itineraryAlertBgColor: darkerDeepBlue,
  itineraryAlertLinkColor: lighterBlue,
  itineraryAlertLinkHoverColor: lightestBlue,
  itineraryAlertDateColor: lighterGray,

  // Skeletons

  skeletonBase: lightDeepBlue,
  skeletonHighlight: lighterDeepBlue,
  skeletonBaseAlt: lightDeepBlue,
  skeletonHighlightAlt: lighterDeepBlue,

  // Logo helpers
  fullLogoText: white,
  fullLogoTextAlt: white,

  // The code above is the new dark mode, organized by component
  // The code below is the old dark mode, which should be replaced by the new dark mode

  mainColor: '#A6D4FF',
  secondaryColor: '#E0F0FF',
  tertiaryColor: '#595959',
  backgroundColor: '#1f1f1f',
  secondaryBackgroundColor: '#2b2b2b',
  primaryFontColor: '#fff',
  primaryFontColorReverse: '#1f1f1f',
  secondaryFontColor: '#ccc',
  popperFontColor: '#b3b3b3',
  hoverColor: '#333',
  activeColor: '#222',
  hoverColorInverted: '#efefef',
  activeColorInverted: '#dedede',
  focusColorInverted: '#dedede',
  badgeBorderColor: 'transparent',
  successColor: '#007e33',
  actionColor: '#5c97d1',
  borderColor: '#2b2b2b',
  buttonColor1: '#008ab0',
  buttonColor1Hover: '#007101',
  buttonColor2: '#efefef',
  buttonText1: 'white',
  buttonText2: '#555',
  inverseSecondaryButtonBgColor: '#dedede', // TODO PRJ77-819 needs color update
  inverseSecondaryButtonBgColorHover: '#cecece', // TODO PRJ77-819 needs color update
  inverseBackgroundColor1: '#084c8d',
  inverseBackgroundColor2: '#0a559e',
  inverseBackgroundColor3: '#003d78',
  inverseBackgroundColor4: '#002345',
  inverseFontColor1: '#ecf6ff',
  inverseFontColor2: '#A5C0D5',
  inverseFontColor3: '#084c8d', // TODO PRJ77-819 needs color update
  inverseButtonColor1: '#3F70A2',
  inverseButtonColor2: '#6896c4',
  inverseButtonColor3: '#86abd0',
  inactiveColor: '#808080',
  inputBorderColor: '#cecece', // TODO PRJ77-819 needs color update
  inputBorderColorFocus: '#000', // TODO PRJ77-819 needs color update
  inputBorderColorReverse: '#3F70A2', // TODO PRJ77-819 needs color update
  inputBorderColorFocusReverse: '#fff', // TODO PRJ77-819 needs color update
  inputBgColorReverse: '#f5f5f5',
  inputBgColorReverseFocus: '#e1e1e1',
  stroke: '#595959',
  cardBackgroundColor: '#0D5EAC',
  tripModeLegacyBg: '#0D5EAC',
  dueTextColor: '#4E802D',
  skeletonBaseColor: '#d5d5d5',
} as const;

/**
 * The LEGACY THEME will be retro fitted to match the old light theme. Then we will decommision when theme migration is complete.
 */

export const LEGACY_THEME: DefaultTheme = {
  ...BASE_THEME,
  name: 'Legacy',

  // Buttons

  // -> Primary Button
  primaryButtonBg: darkGreen,
  primaryButtonBgHover: darkerGreen,
  primaryButtonBorder: darkGreen,
  primaryButtonBorderHover: darkerGreen,
  primaryButtonText: white,
  // -> Outline Button
  outlineButtonBg: 'transparent',
  outlineButtonBgHover: BASE_THEME.white_15,
  outlineButtonBorder: white,
  outlineButtonBorderHover: white,
  outlineButtonText: white,
  outlineButtonTextHover: white,
  // -> Outline Reverse Button
  outlineReverseButtonBg: 'transparent',
  outlineReverseButtonBgHover: charcoal,
  outlineReverseButtonBorder: charcoal,
  outlineReverseButtonBorderHover: charcoal,
  outlineReverseButtonText: charcoal,
  outlineReverseButtonTextHover: white,
  // -> Tertiary Button
  tertiaryButtonBg: lightestDeepBlue,
  tertiaryButtonBgHover: lighterDeepBlue,
  tertiaryButtonBorder: lightBlue,
  tertiaryButtonBorderHover: lightBlue,
  tertiaryButtonText: lightestGray,
  tertiaryButtonTextHover: white,
  // -> Tertiary Reverse Button
  tertiaryReverseButtonBg: white,
  tertiaryReverseButtonBgHover: white,
  tertiaryReverseButtonBorder: gray,
  tertiaryReverseButtonBorderHover: darkGray,
  tertiaryReverseButtonText: lightestDeepBlue,
  tertiaryReverseButtonTextHover: lightDeepBlue,
  // => Text Button
  textButtonBgHover: darkerGreen,
  textButtonBg: 'transparent',
  textButtonBorder: 'transparent',
  textButtonBorderHover: darkerGreen,
  textButtonText: white,
  textButtonTextHover: white,
  // => Text Reverse Button
  textReverseButtonBgHover: darkerGreen,
  textReverseButtonBg: 'transparent',
  textReverseButtonBorder: 'transparent',
  textReverseButtonBorderHover: darkerGreen,
  textReverseButtonText: charcoal,
  textReverseButtonTextHover: white,
  // -> Map Button
  mapButtonBg: white,
  mapButtonIcon: darkerGray,
  mapButtonText: darkerGray,
  mapButtonIconHover: black,
  mapButtonTextHover: black,
  mapButtonShadow: `${mapElementShadow} ${gray}`,
  mapButtonShadowHover: `${mapElementShadowHover} ${darkGray}`,

  // Text-link
  textLink: lightestBlue,
  textLinkHover: white,

  // Text-link-reverse
  textLinkReverse: lightestDeepBlue,
  textLinkReverseHover: lightDeepBlue,

  // Map Focus
  mapFocusOutline: black,

  // Menu
  menuBg: blue,
  menuWrapperBg: charcoal,
  menuPrimarySecondaryText: white,
  menuSecondaryLabels: lightestBlue,
  menuIconFill: white,
  menuAnimatedUnderline: green,
  menuFooterText: gray,
  menuFooterTextHover: white,
  menuFooterBullet: white,
  menuPromoChevronRight: orange,
  menuPromoText: white,
  menuMobileCloseIcon: white,
  menuPromoOverlay: `linear-gradient(${imageOverlayFrom} 0%, ${imageOverlayTo} 100%)`,
  menuNavBg: blue,
  menuLogoText: white,

  // Navbar (Logo, Title, Save/Close Buttons)
  navbarBg: blue,
  navbarButtonHover: darkerBlue,
  navbarText: white,
  navbarTextHover: BASE_THEME.white_50,
  navbarMenuButtonBg: darkGreen,
  navbarMenuButtonBgHover: darkerGreen,
  navbarStripe: orange,

  // Skip Nav Link
  skipNavLinkBg: darkGreen,
  skipNavLinkText: white,

  // DetailNavHeader (Back, MinMax, Close buttons)
  navHeaderBg: blue,
  navHeaderTitle: white,
  navIconFill: white,
  navIconBgHover: darkerBlue,
  navMinMaxBg: blue,
  navMinMaxBgHover: darkBlue,
  navMinMaxBorder: darkBlue,

  // SidePanel content
  contentBg: contentLegacyBg,
  contentBgAlt: lightestGray,
  contentBgHeader: contentLegacyBg,
  contentBgDarken: lightestGray,
  contentChevronDivider: lightGray,
  contentHeaderBg: lightGray,
  contentText: white,
  contentTextAlt: charcoal,
  contentTextDim: lightGray,
  contentTextDimAlt: darkerGray,
  contentTextError: lighterYellow,
  contentTextInfo: lightestGray,
  contentDivider: lightGray,
  contentPanelShadow: `${contentPanelShadow} ${BASE_THEME.black_50}`,

  // Tabs
  tabListBg: contentLegacyBg,
  tabText: lightestBlue,
  tabTextActive: white,
  tabBorderActive: lighterGreen,

  // Card
  cardBg: white,
  cardBorder: lightGray,
  cardShadow: `${contentElementShadow} ${gray}`,
  cardShadowHover: `${contentElementShadowHover} ${darkGray}`,
  cardExternalLink: charcoal,
  cardExternalLinkBgHover: lighterGray,
  cardText: charcoal,
  cardTextDim: darkerGray,
  cardOutlineBorder: white,
  cardOutlineBorderHover: white,

  // Arrivals
  arrivalSchBg: lighterGray,
  arrivalBorder: charcoal,
  arrivalEstText: charcoal,
  arrivalSchText: darkerGray,
  arrivalTrackText: white,
  arrivalTrackBg: darkActionBlue,
  arrivalTrackBgHover: darkerActionBlue,
  arrivalTrackDropoffCanceledText: white,
  arrivalDropoffCanceled: darkRed,
  arrivalDropoffCanceledDarken: darkerRed,

  // Favorite star
  favoriteStarFill: darkRed,
  favoriteStarStroke: charcoal,

  // Input text
  inputBg: white,
  inputText: charcoal,
  inputIconFill: darkerGray,
  inputIconFillHover: black,
  inputBorder: `1px solid ${lightGray}`,
  inputTextPlaceholder: darkerGray,
  inputOptionalText: lighterGray,

  // Map Popup
  mapPopupShadow: `drop-shadow(${mapElementShadow} ${gray})`,
  mapPopupBg: white,
  mapPopupText: charcoal,
  mapPopupTextLabel: darkerGray,
  mapPopupButtonBgHover: lightestGray,
  mapPopupButtonBorder: gray,
  mapPopupButtonHover: darkerGreen,
  mapPopupButtonText: charcoal,
  mapPopupButtonTextHover: white,
  mapPopupClose: darkGray,
  mapPopupCloseHover: black,

  // Icon Helpers
  biketownRentalLogoFill: biketownRentalLogoOrange,
  exclamationMutedIconBg: charcoal,
  transitTrackerIconFill: white,
  extraMapControlsIconFill: white,

  // PopoverMenuList
  popoverMLEllipsesFill: lightestGray,
  popoverMLEllipsesFillHover: white,
  popoverMLBg: white,
  popoverMLBorder: lighterGray,
  popoverMLItemHover: darkGreen,
  popoverMLText: charcoal,
  popoverMLTextHover: white,

  // Vehicle tracking
  vehicleTrackerHighlightColor: actionBlue,
  vehicleTrackerLowlightColor: lightestBlue,
  vehicleTrackerHighlightBackingColor: lightestGray,
  vehicleTrackerBaseColor: transparent,
  vehicleContentSelectedStopHighlightBg: darkActionBlue,
  vehicleContentSelectedStopHighlightArrivalText: darkActionBlue,
  vehicleContentSelectedStopHighlightText: white,
  vehicleContentSelectedMapIconFill: lighterGray,
  vehicleContentSelectedMapIconFillHover: white,
  vehicleContentDropOffOnlyBg: darkRed,
  vehicleContentDropOffOnlyText: white,

  // Toast
  toastCardShadow: `${mapElementShadow} ${gray}`,
  toastCloseFrontCircle: charcoal,
  toastCloseBackCircle: white,
  toastCloseFill: darkerGray,
  toastText: charcoal,

  // Inputs: Drop downs, all things around and within...
  dropDownContentBg: white,
  dropDownInputBg: white,
  dropDownIndicator: charcoal,
  dropDownText: charcoal,
  dropDownTextSelected: white,
  dropDownTextDim: gray,
  dropDownLabelText: white,
  dropDownBorder: lightGray,
  dropDownInputRadius: BASE_THEME.inputOpenRadius,
  dropDownContentRadius: BASE_THEME.inputDropDownRadius,
  dropDownInputShadow: BASE_THEME.inputDropDownShadow,
  dropDownContentShadow: BASE_THEME.inputDropDownShadow,
  dropDownOptionSelectedCheck: lightestGreen,
  dropDownOptionSelected: darkGreen,
  dropDownOptionSelectedHover: lightGray,
  dropDownTimeInputFocus: lightGray,
  dropDownTimeDateTabFocus: darkGreen,
  dropDownEditBg: lighterGray,
  dropDownEditBgHover: lightestGray,
  dropDownSaveCloseBgHover: lighterGray,

  // TODO: (PRJ77-727) Update these with actual colors for the given theme
  // Checkbox
  checkboxBorder: lightGray,
  checkboxBorderHover: gray,
  checkboxCheck: white,
  checkboxCheckHover: white,
  checkboxCheckDisabled: white,
  checkboxCheckedBg: darkGreen,
  checkboxLabelText: white,

  // Map Attribution
  mapAttributionDesktopText: darkerGray,
  mapAttributionDesktopTextHover: black,
  mapAttributionDesktopBg: white,
  mapAttributionMobileText: gray,
  mapAttributionMobileTextHover: white,
  mapAttributionMobileBg: black,

  // Toggle Options (Used primary in Map Settings)
  toggleOptionBgHover: darkBlue,
  toggleOptionBorder: lightGray,
  toggleOptionBorderActive: lighterGray,
  toggleOptionCheckCircleBg: 'transparent',
  toggleOptionCheckCircleBgActive: darkGreen,
  toggleOptionCheckFill: white,
  toggleOptionText: white,

  // Dialog (Map Settings, etc.)
  dialogBg: blue,
  dialogNavText: white,
  dialogText: white,

  // Itinerary

  // -> Trip Summary
  itineraryTripSummaryBg: lighterGray,
  // => NotificationContainer
  itineraryTextRealtime: darkActionBlue,
  // -> ItineraryBody
  itineraryDividerBorder: lightGray,
  itineraryTipBg: lighterGray,
  itineraryTipLink: blue,
  itineraryExternalTripLink: lightestDeepBlue,
  itineraryExternalTripLinkHover: lightDeepBlue,
  // -> Promos
  itineraryPromoLink: blue,
  itineraryPromoLinkHover: darkBlue,
  // -> Footer
  itineraryFooterBg: contentLegacyBg,
  itineraryLegalLink: lightestBlue,
  itineraryLegalLinkHover: white,
  itineraryFooterButtonBg: blue,
  itineraryFooterButtonBgHover: darkerBlue,
  itineraryFooterButtonText: white,
  itineraryFooterButtonBorder: darkBlue,

  // -> Alerts
  itineraryAlertToggleText: red,
  itineraryAlertTextColor: charcoal,
  itineraryAlertBgColor: lighterGray,
  itineraryAlertLinkColor: blue,
  itineraryAlertLinkHoverColor: darkerBlue,
  itineraryAlertDateColor: darkerGray,

  // Skeletons

  skeletonBase: lightGray,
  skeletonHighlight: lightestGray,
  skeletonBaseAlt: blue,
  skeletonHighlightAlt: lightBlue,

  // Logo helpers
  fullLogoText: white,
  fullLogoTextAlt: blue,

  // This is the legacy theme which was a copy of the light theme at some point.
  // This LEGACY_THEME should look like the old light theme, eventually this entire
  // theme will be retired.

  mainColor: '#084c8d',
  secondaryColor: '#385979',
  tertiaryColor: '#e8eef4',
  backgroundColor: '#f5f5f5',
  secondaryBackgroundColor: '#ffffff',
  primaryFontColor: '#222',
  primaryFontColorReverse: '#fff',
  secondaryFontColor: '#575757',
  popperFontColor: '#707070',
  hoverColor: '#f5f5f5',
  activeColor: '#dedede',
  hoverColorInverted: '#4573a1',
  activeColorInverted: '#28598a',
  focusColorInverted: '#5c8ab9',
  badgeBorderColor: 'transparent',
  successColor: '#008ab0',
  actionColor: '#4777a6',
  borderColor: '#efefef',
  buttonColor1: '#0080A3',
  buttonColor1Hover: '#007191',
  buttonColor2: '#efefef',
  buttonText1: '#fff',
  buttonText2: '#555',
  inverseSecondaryButtonBgColor: '#dedede',
  inverseSecondaryButtonBgColorHover: '#cecece',
  inverseBackgroundColor1: '#084c8d',
  inverseBackgroundColor2: '#0a559e',
  inverseBackgroundColor3: '#003d78',
  inverseBackgroundColor4: '#002B54',
  inverseFontColor1: '#ecf6ff',
  inverseFontColor2: '#A5C0D5',
  inverseFontColor3: '#084c8d',
  inverseButtonColor1: '#3F70A2',
  inverseButtonColor2: '#6896c4',
  inverseButtonColor3: '#86abd0',
  inactiveColor: '#808080',
  inputBorderColor: '#cecece',
  inputBorderColorFocus: '#000',
  inputBorderColorReverse: '#3F70A2',
  inputBorderColorFocusReverse: '#fff',
  inputBgColorReverse: '#084c8d',
  inputBgColorReverseFocus: '#003d78',
  stroke: '#e8eef4',
  cardBackgroundColor: '#0D5EAC',
  tripModeLegacyBg: '#0D5EAC',
  dueTextColor: '#4E802D',
  skeletonBaseColor: '#d5d5d5',
} as const;
